/**
 * PDP 혜택 카드의 "Add More Badges" 를 눌렀을 때 **어디로 스크롤할지** 계산한다.
 *
 * ## 왜 순수 함수로 빼는가
 *
 * 종전 구현은 `#buy-section`(갤러리+레일 컨테이너) 최상단으로 스크롤했다. 그 근거는
 * "우측 레일은 sticky 라 레일 내부 요소로의 `scrollIntoView` 가 창을 안 움직인다" 였는데
 * 전제가 틀렸고, 결과적으로 배지 영역보다 **한 화면 이상 위**에 착지했다.
 *
 * ⚠️ 이 계산은 `scrollY + rectTop` 이 요소의 **문서 좌표**라고 전제한다. 배지 아코디언이
 * 들어 있는 레일이 sticky 로 pin 되면 그 전제가 깨진다(pin 된 요소는 스크롤을 따라
 * 올라온다) — 계산은 통과하고 화면만 틀린다, #5264 와 **구분이 안 되는 증상**이다.
 *
 * **#5270 이 sticky 를 좌측 갤러리로 옮기면서 레일에서 `laptop:sticky` 를 아예
 * 제거했다** — 레일은 일반 흐름이고 배지 아코디언도 그 안이라 pin 될 일이 없다.
 * (그 전까지는 "레일이 갤러리보다 높아 travel 0" 이라는 **우연**에 기대고 있었고, 짧은
 * 레일 제품이 생기면 깨지는 상태였다. 지금은 조건 자체가 사라졌다.)
 * 레일에 `laptop:sticky` 를 다시 넣으면 이 전제가 깨진다 — sentinel(구매바 노출 타이밍)도
 * 같은 트리거로 함께 깨지므로 둘을 같이 봐야 한다. 회귀는
 * `__tests__/pages/buy-tactsuit-scroll-anchors.test.ts` 가 잠근다.
 *
 * 실측(#5264, localhost, `tactsuit-pro`):
 *
 * | 뷰포트 | 착지 스크롤 | 착지 후 배지 영역 top | 결과 |
 * | --- | --- | --- | --- |
 * | 1440×900 | 177 | 1064 | 화면 밖 164px |
 * | 375×812 | 199 | 1503 | 화면 밖 691px (≈1.85화면) |
 *
 * 계산을 DOM 에서 떼어내면 이 표를 테스트로 고정할 수 있다. 페이지 컴포넌트는 훅을
 * 30개 넘게 끌어와서 이것만 검증할 방법이 없다.
 *
 * ## 상단 가림 높이(`occludedTop`)를 상수로 두지 말 것
 *
 * 뷰포트 위쪽은 **스티키 헤더 스택(`#buy-header-stack`)** + 스크롤 시 내려오는
 * **`#compact-buy-bar`** 가 가린다. 실측 높이가 브레이크포인트마다 다르다
 * (헤더 스택 데스크톱 137 / 모바일 175, 바 65). 배너 유무로도 달라지므로
 * (`CompactBuyBar` 가 같은 이유로 `ResizeObserver` 를 쓴다) 호출처가 **실측해서**
 * 넘긴다.
 */
export interface BadgeScrollInput {
  /** 현재 `window.scrollY` */
  scrollY: number;
  /** `window.innerHeight` */
  viewportHeight: number;
  /** 배지 아코디언의 `getBoundingClientRect().top` */
  rectTop: number;
  /** 배지 아코디언의 `getBoundingClientRect().bottom` */
  rectBottom: number;
  /** 스티키 헤더 + 컴팩트 구매바 등 상단을 가리는 높이의 합 */
  occludedTop: number;
  /** 가림 요소 바로 아래에 두지 않기 위한 여백 */
  margin?: number;
}

export const BADGE_SCROLL_MARGIN = 12;

/**
 * 목표 `scrollY`. **이미 온전히 보이면 `null`** — 화면을 흔들지 않는다.
 *
 * ⚠️ `null` 과 `0` 을 구분해야 한다. 페이지 최상단이 목표인 경우가 실제로 있어서
 * (`0`) falsy 검사로 분기하면 그 케이스가 "스크롤 안 함" 으로 삼켜진다.
 */
export function computeBadgeScrollTop({
  scrollY,
  viewportHeight,
  rectTop,
  rectBottom,
  occludedTop,
  margin = BADGE_SCROLL_MARGIN,
}: BadgeScrollInput): number | null {
  const safeTop = occludedTop + margin;

  // 가림 요소 아래에 위쪽이 들어와 있고 아래쪽도 화면 안이면 그대로 둔다.
  //
  // ⚠️ **수용한 한계**: 호출처는 펼침 애니메이션(`animate-accordion-down 0.2s`)이 끝나기
  // 전에 재므로 `rectBottom` 은 **접힌 높이**다. 그래서 이 판정은 체계적으로 낙관적이다 —
  // 펼치면 화면을 넘칠 상황인데 "이미 보임 → null" 로 떨어져 아무 것도 안 할 수 있다.
  // 걸리려면 접힌 배지 영역 전체가 들어오는 **아주 긴 뷰포트**여야 하는데(클릭 지점인
  // 혜택 카드는 항상 배지보다 아래라 보통 `rectTop` 이 음수다) 실측 두 뷰포트에서는
  // 재현되지 않았다. 스크롤 정지 후 재측정 보정을 넣으면 이 한계와 위 pin 케이스를 함께
  // 흡수할 수 있지만 타이머 + 정리 로직이 늘고 이 하니스에서 검증이 어려워 **의도적으로
  // 미룬다.** 착지가 어긋난다는 신고가 다시 오면 재측정 보정을 먼저 검토할 것.
  const fullyVisible = rectTop >= safeTop && rectBottom <= viewportHeight;
  if (fullyVisible) return null;

  // 배지 영역 **상단**을 가림 요소 바로 아래에 놓는다. 아코디언이 펼쳐지며 커지는 건
  // 아래쪽이라 상단 기준이 안전하다 — 높이를 기준으로 잡으면 펼침 애니메이션 중
  // 측정값이 흔들린다.
  return Math.max(0, scrollY + rectTop - safeTop);
}
